import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser, subscribeToAuth } from '../lib/auth';
// useAuth — current logged-in user + login/logout/signup

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    // Go through the shared cache rather than calling getSession() directly.
    // Eight components use this hook, and each direct call took the auth lock
    // and left an unguarded promise: when the lock aborts on a slow load the
    // rejection had no handler, surfacing as an unhandled AbortError.
    getCurrentUser()
      .then(u => {
        if (!alive) return;
        setUser(u);
        setLoading(false);
      })
      .catch(err => {
        if (!alive) return;
        console.error('useAuth: initial session lookup failed:', err?.message || err);
        setUser(null);
        setLoading(false);
      });

    // Listen for auth changes (login, logout, token refresh) through the app's
    // single Supabase listener. Registering one here per component queued them
    // all on the same auth lock and produced an uncatchable AbortError on slow
    // loads — see the comment in lib/auth.js.
    const unsubscribe = subscribeToAuth((_event, session) => {
      if (!alive) return;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => { alive = false; unsubscribe(); };
  }, []);

  const signUp = async (email, password, displayName, username) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName, username },
        // Send them back to wherever they signed up from. Hardcoding production
        // meant confirming from a Vercel preview dropped you on the live site,
        // so anything tested on a preview was silently tested against main.
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  // Saves the new password for whoever is signed in. On /reset-password that is
  // the temporary session the reset link opened.
  const updatePassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  // Sends the confirmation email again. Supabase allows one per minute per
  // address and says so in the error, which the caller shows as it is.
  const resendVerification = async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    return { error };
  };

  return { user, loading, signUp, signIn, signOut, resetPassword, updatePassword, resendVerification };
}


// useProfile — current user's reader profile

export function useProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetch() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) setError(error.message);
      else setProfile(data);
      setLoading(false);
    }
    fetch();
  }, []);

  const updateProfile = async (updates) => {
    const user = await getCurrentUser();
    if (!user) return { error: 'Not logged in' };

    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .select()
      .single();

    if (!error) setProfile(data);
    return { data, error };
  };

  // Save archetype from the ArchetypeEditModal
  const saveArchetype = async (emoji, label) => {
    return updateProfile({ archetype_emoji: emoji, archetype_label: label });
  };

  // Writes a `preferences` column on the profile. NOT the user_preferences
  // table, and not the "What's your type" panel, which no longer exists.
  // Nothing calls this — it was already dead before that panel was removed.
  const savePreferences = async (preferences) => {
    return updateProfile({ preferences });
  };

  return { profile, loading, error, updateProfile, saveArchetype, savePreferences };
}


// useCircle — people the current user follows + their activity

export function useCircle() {
  const [following, setFollowing] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      // Get list of people the user follows
      const { data: followData } = await supabase
        .from('follows')
        .select('following_id, profiles!follows_following_id_fkey(id, display_name, username, avatar_url)')
        .eq('follower_id', user.id);

      setFollowing(followData?.map(f => f.profiles) || []);

      // Get their recent library activity
      if (followData?.length) {
        const followingIds = followData.map(f => f.following_id);
        const { data: activityData } = await supabase
          .from('user_library')
          .select(`
            shelf, date_added, rating,
            profiles!user_library_user_id_fkey(id, display_name, username),
            books(id, title, author)
          `)
          .in('user_id', followingIds)
          .order('date_added', { ascending: false })
          .limit(20);

        setActivity(activityData || []);
      }

      setLoading(false);
    }
    fetch();
  }, []);

  const follow = async (userId) => {
    const user = await getCurrentUser();
    if (!user) return;
    await supabase.from('follows').insert({ follower_id: user.id, following_id: userId });
    setFollowing(prev => [...prev, { id: userId }]);
  };

  const unfollow = async (userId) => {
    const user = await getCurrentUser();
    if (!user) return;
    await supabase.from('follows').delete()
      .eq('follower_id', user.id).eq('following_id', userId);
    setFollowing(prev => prev.filter(f => f.id !== userId));
  };

  return { following, activity, loading, follow, unfollow };
}


// useQuoteOfTheDay — fetch today's approved quote + submit new ones

export function useQuoteOfTheDay() {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('quote_submissions')
        .select('id, quote_text, book_title, author, likes')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Fallback to static quote if none in DB yet
      setQuote(data || {
        quote_text: 'We are all fools in love.',
        book_title: 'Pride and Prejudice',
        author: 'Jane Austen',
        likes: 234,
      });
      setLoading(false);
    }
    fetch();
  }, []);

  const submitQuote = async ({ quoteText, bookTitle, author }) => {
    const user = await getCurrentUser();

    const { error } = await supabase.from('quote_submissions').insert({
      user_id: user?.id || null,
      quote_text: quoteText,
      book_title: bookTitle,
      author,
      status: 'pending',
    });

    return { error };
  };

  return { quote, loading, submitQuote };
}


// useGiveaway — active giveaway + entry flow

export function useGiveaway() {
  const [giveaway, setGiveaway] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      // Get the current active giveaway
      const { data } = await supabase
        .from('giveaways')
        .select('*')
        .eq('active', true)
        .gte('ends_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      setGiveaway(data);

      // Check if user has already entered
      if (data) {
        const user = await getCurrentUser();
        if (user) {
          const { data: entry } = await supabase
            .from('giveaway_entries')
            .select('id')
            .eq('giveaway_id', data.id)
            .eq('user_id', user.id)
            .single();
          setHasEntered(!!entry);
        }
      }

      setLoading(false);
    }
    fetch();
  }, []);

  const enterGiveaway = async (email) => {
    const user = await getCurrentUser();
    if (!user || !giveaway) return { error: 'Not logged in or no active giveaway' };

    const { error } = await supabase.from('giveaway_entries').insert({
      giveaway_id: giveaway.id,
      user_id: user.id,
      email,
      entry_count: 1,
    });

    if (!error) setHasEntered(true);
    return { error };
  };

  return { giveaway, hasEntered, loading, enterGiveaway };
}